import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toggleFullscreen, togglePictureInPicture } from './videoPlayerUtils';
import VideoPlayerControls from './VideoPlayerControls';
import './CustomVideoPlayer.css';

const CustomVideoPlayer = ({ src, subtitleSrc, videoType = 'MANUAL', logoUrl, onDownload, className = '' }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) { videoRef.current.play(); setIsPlaying(true); }
      else { videoRef.current.pause(); setIsPlaying(false); }
    }
  };

  const handleProgressClick = (e) => {
    if (videoRef.current && duration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const newTime = ((e.clientX - rect.left) / rect.width) * duration;
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = Number.parseFloat(e.target.value);
    setVolume(newVolume);
    if (videoRef.current) { videoRef.current.volume = newVolume; setIsMuted(newVolume === 0); }
  };

  const handleMuteToggle = () => {
    if (videoRef.current) {
      if (isMuted) { videoRef.current.volume = volume || 0.5; setIsMuted(false); }
      else { videoRef.current.volume = 0; setIsMuted(true); }
    }
  };

  const handleFullscreen = () => toggleFullscreen(containerRef, isFullscreen);
  const handlePictureInPicture = async () => await togglePictureInPicture(videoRef.current);

  const handleDownloadClick = () => { onDownload?.(); setShowMenu(false); };

  const handleSeekArrow = (direction) => {
    if (videoRef.current) videoRef.current.currentTime += direction === 'right' ? 5 : -5;
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let rafId;
    const updateProgress = () => { setCurrentTime(video.currentTime); rafId = requestAnimationFrame(updateProgress); };
    const start = () => { rafId = requestAnimationFrame(updateProgress); };
    const stop = () => cancelAnimationFrame(rafId);

    const handleLoadedMetadata = () => setDuration(video.duration);
    const handlePlay = () => { setIsPlaying(true); start(); };
    const handlePause = () => { setIsPlaying(false); stop(); };
    const handleEnded = () => { setIsPlaying(false); stop(); };
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);

    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      cancelAnimationFrame(rafId);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!containerRef.current?.contains(document.activeElement)) return;
      switch (e.key) {
        case ' ': e.preventDefault(); handlePlayPause(); break;
        case 'f': handleFullscreen(); break;
        case 'm': handleMuteToggle(); break;
        case 'ArrowRight': handleSeekArrow('right'); break;
        case 'ArrowLeft': handleSeekArrow('left'); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [volume, isMuted]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {videoType !== 'MANUAL' && logoUrl && (
        <div className="custom-video-player__logo"><img src={logoUrl} alt="Video logo" /></div>
      )}
      <div ref={containerRef} className={`custom-video-player ${className}`} role="application" tabIndex={0}>
        <video ref={videoRef} className="custom-video-player__video" src={src}>
          {subtitleSrc && <track label="English" kind="subtitles" srcLang="en" src={subtitleSrc} default />}
          Your browser does not support the video tag.
        </video>
        <VideoPlayerControls
          isPlaying={isPlaying} currentTime={currentTime} duration={duration}
          volume={volume} isMuted={isMuted} isFullscreen={isFullscreen}
          showMenu={showMenu} setShowMenu={setShowMenu}
          onPlayPause={handlePlayPause} onProgressClick={handleProgressClick}
          onVolumeChange={handleVolumeChange} onMuteToggle={handleMuteToggle}
          onFullscreen={handleFullscreen} onPictureInPicture={handlePictureInPicture}
          onSeekArrow={handleSeekArrow} onDownload={onDownload ? handleDownloadClick : undefined}
        />
      </div>
    </div>
  );
};

CustomVideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  subtitleSrc: PropTypes.string, videoType: PropTypes.string,
  logoUrl: PropTypes.string, onDownload: PropTypes.func, className: PropTypes.string
};

export default CustomVideoPlayer;
